import { FunctionComponent, useContext, useEffect, useMemo, useState } from 'react'
import { Box, Dialog, DialogContent, DialogTitle, Typography } from '@mui/material'
import { AddManualSpendBasedDialog } from './add-manual-spend-based-dialog'
import { IEmissionDetail } from '../../../shared/services/emission/emission.model'
import { IEmissionCalculation } from '../../../shared/services/emission-calculation/emission-calculation.model'
import { EmissionsInputDirect } from '../../presentation-components/emissons-components/overview/emissions-input/emissions-input-direct'
import { EmissionsInputGoods } from '../../presentation-components/emissons-components/overview/emissions-input/emissions-input-goods'
import { EmissionsInputFilter } from '../../presentation-components/emissons-components/overview/emissions-input/emissions-input-filter'
import { LoadingSpinner } from '../../presentation-components/display-elements-components/loading-spinner'
import SubsidiaryService from '../../../shared/services/subsidiary/subsidiary.service'
import { ISubsidiary } from '../../../shared/services/subsidiary/subsidiary.model'
import GroupService from '../../../shared/services/group/group.service'
import { IGroupDetail } from '../../../shared/services/group/group.model'
import ConversionSourceService from '../../../shared/services/conversion-source/conversion-source.service'
import { IConversionSource } from '../../../shared/services/conversion-source/conversion-source.model'
import EmissionCalculationService from '../../../shared/services/emission-calculation/emission-calculation.service'
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications'
import { useIsReportPeriodLocked } from '../../../hooks/use-is-report-period-locked'
import { notifyError } from '../../../shared/utils/notify-error'

export const EditEmissionCalculationDialog: FunctionComponent<{
  open: boolean,
  calculation: IEmissionCalculation | null,
  calculationIds: string[],
  calculations: IEmissionCalculation[],
  emission: IEmissionDetail,
  closeHandler: () => void,
  successHandler: (emissionCalculationIds?: string[]) => void,
}> = ({
  open,
  calculation,
  calculationIds,
  calculations,
  emission,
  closeHandler,
  successHandler,
}) => {
  const emissionCalculationService = new EmissionCalculationService()
  const userNotificationsContext = useContext(UserNotificationsContext)
  const isReportPeriodLocked = useIsReportPeriodLocked()
  const [group, setGroup] = useState<IGroupDetail>()
  const [conversionSources, setConversionSources] = useState<IConversionSource[]>([])
  const [subsidiaries, setSubsidiaries] = useState<ISubsidiary[]>([])
  const [selectedSubsidiaryId, setSelectedSubsidiaryId] = useState<string | undefined>()
  const [selectedEmissionFactorSourceId, setSelectedEmissionFactorSourceId] = useState<string | undefined>()
  const [isSubmittingActivity, setIsSubmittingActivity] = useState(false)
  const [isLoadingActivityForm, setIsLoadingActivityForm] = useState(false)

  const editCalculations = useMemo(
    () => calculations.filter((existingCalculation) => calculationIds.includes(existingCalculation.id)),
    [calculations, calculationIds],
  )

  const referenceCalculation = useMemo(() => {
    if (!calculation) return null
    if (calculation.type === 'SPEND' || calculation.type === 'ACTIVITY') {
      return editCalculations.find((existingCalculation) =>
        existingCalculation.type === calculation.type &&
        !existingCalculation.isWtt &&
        !existingCalculation.isMarketBased,
      ) ?? calculation
    }
    return calculation
  }, [calculation, editCalculations])
  // Certificates can be linked to any of the emission's calculations, so aggregate across the
  // full edit set for the form's initial selection.
  const attachedRenewableEnergyCertificateIds = useMemo(
    () => Array.from(new Set(
      editCalculations.flatMap((existingCalculation) =>
        (existingCalculation.transactionRenewableEnergyCertificates ?? [])
          .map((link) => (link as unknown as { renewableEnergyCertificateId?: string }).renewableEnergyCertificateId)
          .filter((id): id is string => Boolean(id)),
      ),
    )),
    [editCalculations],
  )
  const activityCategoryId = referenceCalculation?.categoryId ?? referenceCalculation?.category?.id ?? ''
  const activityConversionSourceId = referenceCalculation?.conversion?.conversionSourceId

  useEffect(() => {
    if (!open || !referenceCalculation) return

    setGroup(undefined)
    setConversionSources([])
    setIsLoadingActivityForm(referenceCalculation.type === 'ACTIVITY')

    SubsidiaryService.getInstance()
      .getAll()
      .then((response) => {
        setSubsidiaries(response)
        setSelectedSubsidiaryId(emission.subsidiaryId)
      })
      .catch(console.error)

    if (referenceCalculation.type === 'ACTIVITY') {
      GroupService.getInstance()
        .getGroupWithActionsForReportPeriodWithPagination(referenceCalculation.groupId, emission.reportPeriodId, 0, 10)
        .then(setGroup)
        .catch(console.error)

      setSelectedEmissionFactorSourceId(activityConversionSourceId)
      setSelectedSubsidiaryId(emission.subsidiaryId)
      if (activityCategoryId) {
        new ConversionSourceService()
          .getConversionSourceForCategory(
            activityCategoryId,
            'conversion',
            referenceCalculation.scope,
            true,
          )
          .then((response) => {
            setConversionSources(response)
            setIsLoadingActivityForm(false)
          })
          .catch((error) => {
            console.error(error)
            setIsLoadingActivityForm(false)
          })
      } else {
        setIsLoadingActivityForm(false)
      }
    } else {
      setIsLoadingActivityForm(false)
    }
  }, [open, referenceCalculation, emission.reportPeriodId, emission.subsidiaryId, activityCategoryId, activityConversionSourceId])

  if (!open || !calculation || !referenceCalculation) return null

  if (calculation.type === 'SPEND') {
    return (
      <AddManualSpendBasedDialog
        open={open}
        emission={emission}
        editCalculation={referenceCalculation}
        editCalculations={editCalculations}
        editCalculationIds={calculationIds}
        closeHandler={closeHandler}
        successHandler={successHandler}
      />
    )
  }

  const title = calculation.type === 'DIRECT'
    ? 'Edit direct calculation'
    : calculation.type === 'PRODUCT'
      ? 'Edit product calculation'
      : 'Edit activity calculation'

  const handleActivityEditSubmit = (transaction: Record<string, unknown>) => {
    if (isReportPeriodLocked) {
      userNotificationsContext.openDialog({
        active: true,
        title: 'Action Not Allowed',
        message: 'The selected report period is locked and cannot be modified',
        buttonCopy: 'Ok',
        hideCancelButton: true,
      })
      return
    }

    if (isSubmittingActivity) return
    setIsSubmittingActivity(true)
    emissionCalculationService
      .editEmissionCalculation(calculationIds, transaction)
      .then((response) => {
        setIsSubmittingActivity(false)
        userNotificationsContext.openSnackbar({ open: true, message: 'Saved successfully' })
        successHandler(response.emissionCalculationIds)
      })
      .catch((err) => {
        setIsSubmittingActivity(false)
        notifyError(userNotificationsContext.openSnackbar, err, 'saving the calculation')
      })
  }

  return (
    <Dialog open={open} onClose={closeHandler} fullWidth maxWidth="md">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {calculation.type === 'DIRECT' && (
          <EmissionsInputDirect
            categoryId={referenceCalculation.categoryId ?? ''}
            groupId={referenceCalculation.groupId}
            onSave={successHandler}
            subsidiaries={subsidiaries}
            selectedSubsidiaryId={selectedSubsidiaryId ?? emission.subsidiaryId}
            emission={emission}
            editCalculation={referenceCalculation}
            editCalculationIds={calculationIds}
            cancelHandler={closeHandler}
          />
        )}
        {calculation.type === 'PRODUCT' && referenceCalculation.capitalGoodId && (
          <EmissionsInputGoods
            selectedGoodId={referenceCalculation.capitalGoodId}
            subsidiaries={subsidiaries}
            setViewEmissions={() => { }}
            groupId={referenceCalculation.groupId}
            onSave={successHandler}
            emission={emission}
            editCalculation={referenceCalculation}
            editCalculationIds={calculationIds}
            cancelHandler={closeHandler}
          />
        )}
        {calculation.type === 'ACTIVITY' && isLoadingActivityForm && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <LoadingSpinner />
          </Box>
        )}
        {calculation.type === 'ACTIVITY' && !isLoadingActivityForm && !group && (
          <Typography variant="body2" sx={{ color: 'text.secondary', py: 2 }}>
            Unable to load the calculation form.
          </Typography>
        )}
        {calculation.type === 'ACTIVITY' && !isLoadingActivityForm && group && (
          <EmissionsInputFilter
            categoryId={activityCategoryId}
            onSubmit={handleActivityEditSubmit}
            groupId={referenceCalculation.groupId}
            conversionSources={conversionSources}
            emissionFactorSourceHandler={(value) => setSelectedEmissionFactorSourceId(value.id)}
            allowMarketBasedEmissionInput={group.categories.find((category) => category.id === activityCategoryId)?.allowMarketBasedMultiplier}
            isSubmitting={isSubmittingActivity}
            subsidiaries={subsidiaries}
            selectedEmissionFactorSourceId={selectedEmissionFactorSourceId}
            selectedSubsidiaryId={selectedSubsidiaryId ?? emission.subsidiaryId}
            setSelectedSubsidiaryId={setSelectedSubsidiaryId}
            emission={emission}
            editCalculation={referenceCalculation}
            initialRenewableEnergyCertificateIds={attachedRenewableEnergyCertificateIds}
            cancelHandler={closeHandler}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
